// Multicolumn Swiper Implementation
class MulticolumnSwiper {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // Inicializar cuando el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initAllSliders());
    } else {
      this.initAllSliders();
    }
    
    // Observar cambios en el DOM
    this.observeDOMChanges();
  }

  initAllSliders() {
    const containers = document.querySelectorAll('.multicolumn-slider-container');
    
    containers.forEach((container) => {
      if (!container.dataset.swiperInitialized) {
        this.initSwiper(container);
      }
    });
  }

  initSwiper(container) {
    const sectionId = container.dataset.sectionId;
    
    // Verificar que Swiper esté disponible
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initSwiper(container), 100);
      return;
    }
    
    // Verificar que el elemento esté visible
    if (!this.isElementVisible(container)) {
      this.observeElementVisibility(container);
      return;
    }
    
    // Destruir Swiper existente si existe
    if (this.swipers.has(sectionId)) {
      this.swipers.get(sectionId).destroy();
      this.swipers.delete(sectionId);
    }

    const swiperContainer = container.querySelector('.swiper');
    if (!swiperContainer) {
      console.warn(`No se encontró el contenedor de Swiper para la sección ${sectionId}`);
      return;
    }

    // Obtener configuración
    const config = this.getSwiperConfig(container);

    try {
      // Crear nueva instancia de Swiper
      const swiper = new Swiper(swiperContainer, config);
      
      // Guardar referencia
      this.swipers.set(sectionId, swiper);
      
      // Marcar como inicializado
      container.dataset.swiperInitialized = 'true';
      
      // Agregar eventos personalizados
      this.addCustomEvents(swiper, container, sectionId);
      
      console.log(`Swiper inicializado para la sección ${sectionId}`, config);
      
    } catch (error) {
      console.error(`Error al inicializar Swiper para la sección ${sectionId}:`, error);
    }
  }

  isElementVisible(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    
    return rect.top < windowHeight + 100 && rect.bottom > -100;
  }

  observeElementVisibility(element) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.initSwiper(element);
          observer.unobserve(element);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.1
    });
    
    observer.observe(element);
  }

  getSwiperConfig(container) {
    const sectionId = container.dataset.sectionId;
    
    // Valores por defecto y desde data attributes
    const columnsDesktop = parseInt(container.dataset.columnsDesktop) || 3;
    const columnsMobile = parseInt(container.dataset.columnsMobile) || 1;
    const enableAutoplay = container.dataset.enableAutoplay === 'true';
    const autoplayDelay = parseInt(container.dataset.autoplayDelay) || 5000;
    const pauseOnHover = container.dataset.pauseOnHover === 'true';
    const enableLoop = container.dataset.enableLoop === 'true';
    const spaceBetween = parseInt(container.dataset.spaceBetween) || 30;
    const enableSliderDesktop = container.dataset.enableSliderDesktop === 'true';
    const swipeOnMobile = container.dataset.swipeOnMobile === 'true';

    // Solo inicializar si está habilitado para desktop o mobile
    if (!enableSliderDesktop && !swipeOnMobile) {
      return null;
    }

    // Base configuration
    const config = {
      slidesPerView: 'auto',
      spaceBetween: spaceBetween,
      loop: enableLoop,
      centeredSlides: false,
      watchSlidesProgress: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      allowTouchMove: true,
      grabCursor: true,
      breakpoints: {
        0: {
          slidesPerView: columnsMobile,
          slidesPerGroup: 1,
          spaceBetween: Math.max(spaceBetween - 10, 10)
        },
        768: {
          slidesPerView: Math.min(columnsMobile + 1, columnsDesktop),
          slidesPerGroup: 1,
          spaceBetween: Math.max(spaceBetween - 5, 15)
        },
        1024: {
          slidesPerView: columnsDesktop,
          slidesPerGroup: 1,
          spaceBetween: spaceBetween,
          centeredSlides: false
        }
      },
      on: {
        init: (swiper) => {
          this.onSwiperInit(swiper, container, sectionId);
        },
        slideChange: (swiper) => {
          this.onSlideChange(swiper, container, sectionId);
        },
        resize: (swiper) => {
          swiper.update();
        },
        observerUpdate: (swiper) => {
          swiper.update();
        }
      }
    };

    // Agregar autoplay si está habilitado
    if (enableAutoplay) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover
      };
    }

    // Agregar navegación si está habilitado para desktop
    if (enableSliderDesktop) {
      config.navigation = {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev')
      };
      
      config.scrollbar = {
        el: container.querySelector('.swiper-scrollbar'),
        hide: false,
        draggable: true
      };
    }

    return config;
  }

  addCustomEvents(swiper, container, sectionId) {
    // Evento para actualizar contadores
    swiper.on('slideChange', () => {
      this.updateCounters(container, swiper);
    });

    // Evento para pausar/reanudar autoplay
    const pauseOnHover = container.dataset.pauseOnHover === 'true';
    
    if (pauseOnHover && swiper.autoplay) {
      container.addEventListener('mouseenter', () => {
        if (swiper.autoplay && swiper.autoplay.running) {
          swiper.autoplay.stop();
        }
      });
      
      container.addEventListener('mouseleave', () => {
        if (swiper.autoplay && !swiper.autoplay.running) {
          swiper.autoplay.start();
        }
      });
    }

    // Eventos de teclado para accesibilidad
    container.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        swiper.slideNext();
      }
    });

    // Manejar resize de ventana
    const resizeHandler = () => {
      if (swiper && !swiper.destroyed) {
        swiper.update();
      }
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Guardar referencia del handler para limpieza
    swiper.resizeHandler = resizeHandler;
  }

  onSwiperInit(swiper, container, sectionId) {
    // Actualizar contadores iniciales
    this.updateCounters(container, swiper);
    
    // Emitir evento personalizado
    container.dispatchEvent(new CustomEvent('swiper:init', {
      detail: { swiper, sectionId }
    }));
  }

  onSlideChange(swiper, container, sectionId) {
    // Emitir evento personalizado
    container.dispatchEvent(new CustomEvent('swiper:slideChange', {
      detail: { 
        swiper, 
        sectionId,
        activeIndex: swiper.activeIndex,
        realIndex: swiper.realIndex
      }
    }));
  }

  updateCounters(container, swiper) {
    const currentCounter = container.querySelector('.slider-counter--current');
    const totalCounter = container.querySelector('.slider-counter--total');
    
    if (currentCounter) {
      currentCounter.textContent = swiper.realIndex + 1;
    }
    
    if (totalCounter) {
      totalCounter.textContent = swiper.slides.length;
    }
  }

  observeDOMChanges() {
    // Observar cambios en el DOM para detectar nuevas secciones
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Buscar nuevos sliders en el nodo agregado
            const newContainers = node.querySelectorAll ? 
              node.querySelectorAll('.multicolumn-slider-container') : 
              (node.classList && node.classList.contains('multicolumn-slider-container') ? [node] : []);
            
            newContainers.forEach((container) => {
              if (!container.dataset.swiperInitialized) {
                this.initSwiper(container);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Métodos públicos para control externo
  getSwiper(sectionId) {
    return this.swipers.get(sectionId);
  }

  destroySwiper(sectionId) {
    const swiper = this.swipers.get(sectionId);
    if (swiper) {
      // Limpiar event listeners
      if (swiper.resizeHandler) {
        window.removeEventListener('resize', swiper.resizeHandler);
      }
      
      swiper.destroy();
      this.swipers.delete(sectionId);
      
      // Remover marca de inicializado
      const container = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (container) {
        delete container.dataset.swiperInitialized;
      }
    }
  }

  destroyAll() {
    this.swipers.forEach((swiper, sectionId) => {
      if (swiper.resizeHandler) {
        window.removeEventListener('resize', swiper.resizeHandler);
      }
      swiper.destroy();
    });
    this.swipers.clear();
    
    // Remover todas las marcas de inicializado
    document.querySelectorAll('.multicolumn-slider-container').forEach(container => {
      delete container.dataset.swiperInitialized;
    });
  }

  // Método para reinicializar un slider específico
  reinitSwiper(sectionId) {
    const container = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (container) {
      // Remover marca de inicializado para forzar reinicialización
      delete container.dataset.swiperInitialized;
      this.initSwiper(container);
    }
  }

  // Método de limpieza
  destroy() {
    this.destroyAll();
  }
}
